import { api } from 'services/api/api';
import {
  TrackedEventPayload,
  TrackEventsRequest,
} from 'services/api/apis/analytics';
import {
  AnalyticsEvent,
  AnalyticsEventName,
  ONCE_PER_SESSION,
} from './analytics.types';

const SESSION_ID_KEY = 'cl_analytics_session_id';
const FLUSH_DEBOUNCE_MS = 2000;
const FLUSH_BATCH_SIZE = 10;
const MAX_BATCH_SIZE = 50;

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const generateSessionId = (): string => {
  // Prefer crypto.randomUUID where available; fall back to a sufficiently
  // unique string for the rare browsers that lack it. Session IDs are not
  // security-sensitive — they only need to be unique enough to group events.
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  )
    return crypto.randomUUID();

  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const getSessionId = (): string => {
  if (!isBrowser()) return 'ssr';
  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const fresh = generateSessionId();
    window.sessionStorage.setItem(SESSION_ID_KEY, fresh);
    return fresh;
  } catch {
    return generateSessionId();
  }
};

class AnalyticsClient {
  private queue: TrackedEventPayload[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private listenersBound = false;
  private firedOncePerSession = new Set<AnalyticsEventName>();

  public track (event: AnalyticsEvent): void {
    if (!isBrowser()) return;

    if (ONCE_PER_SESSION.has(event.name)) {
      if (this.firedOncePerSession.has(event.name)) return;
      this.firedOncePerSession.add(event.name);
    }

    const props =
      'props' in event && event.props
        ? (event.props as TrackedEventPayload['props'])
        : undefined;

    const payload: TrackedEventPayload = {
      name: event.name,
      occurredAt: new Date().toISOString(),
      sessionId: getSessionId(),
      ...(props ? { props } : {}),
    };

    this.queue.push(payload);
    this.bindLifecycleListeners();

    if (this.queue.length >= FLUSH_BATCH_SIZE) {
      void this.flush();
      return;
    }

    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => {
      void this.flush();
    }, FLUSH_DEBOUNCE_MS);
  }

  /**
   * Flush via axios. Used on the debounced path (page is still alive, so
   * cookies and Authorization headers are available).
   */
  public async flush (): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, MAX_BATCH_SIZE);
    const body: TrackEventsRequest = { events: batch };

    try {
      await api.post('/analytics/events', body);
    } catch {
      // Swallow analytics errors silently — instrumentation must never
      // break the user-visible app. Dropped events are acceptable.
    }
  }

  /**
   * Synchronous flush for `pagehide`/`visibilitychange=hidden` via
   * `navigator.sendBeacon` so events aren't lost on navigation. Falls back
   * to a non-blocking fetch with `keepalive` for browsers without beacon.
   */
  private flushBeacon (): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, MAX_BATCH_SIZE);
    const body: TrackEventsRequest = { events: batch };
    const url = `${process.env.NEXT_PUBLIC_API_URL ?? ''}/analytics/events`;

    try {
      const blob = new Blob([JSON.stringify(body)], {
        type: 'application/json',
      });
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(url, blob)
      )
        return;

      // Beacon refused (rare, e.g. payload too large) — try keepalive fetch.
      void fetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // Best-effort only; intentionally silent.
    }
  }

  private bindLifecycleListeners (): void {
    if (this.listenersBound || !isBrowser()) return;
    this.listenersBound = true;

    const handleHide = (): void => this.flushBeacon();

    window.addEventListener('pagehide', handleHide);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') handleHide();
    });
  }
}

export const analyticsClient = new AnalyticsClient();

/**
 * Top-level convenience API. Most call sites should use this directly; the
 * `useAnalytics` hook only exists for ergonomics in components that already
 * pull other hooks.
 */
export const trackAnalyticsEvent = (event: AnalyticsEvent): void => {
  analyticsClient.track(event);
};
